import React, { FC, useState } from 'react';
import { filter, find, includes, map, sortBy } from 'lodash';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import { Button, SearchInput } from 'src/presentation/components/agent_desktop_components';
import imitateUser from 'src/actions/imitate/imitateUser';
import { FormContentWrapper, FormEditorProps } from 'src/presentation/components/forms/Form';
import { H1, H3 } from '@ad-scripts/presentation/components/common/headers';
import organizationsResource from 'src/actions/imitate/organizationsResource';
import { Spinner } from 'src/presentation/components/pages/shared/Spinner';
import { useResourceValues } from 'src/presentation/hooks/useResource';
import useOrganizationUsers from './useOrganizationUsers';
import { User } from 'src/models/Users/User';

export const ImitateEditor: FC<FormEditorProps> = ({ item }) => {
  const { values: organizations } = useResourceValues(organizationsResource);
  const { loadingState, values: users } = useOrganizationUsers(item.id);

  const organization = find(organizations, o => o.id === item.id);

  return (
    <FormContentWrapper>
      {loadingState === 'succeeded' ? (
        <>
          <H1>Select User in {organization.name}</H1>
          <TwoColumn>
            <Admins orgId={item.id} users={users} />
            <UserPicker orgId={item.id} users={users} />
          </TwoColumn>
        </>
      ) : (
        <Spinner />
      )}
    </FormContentWrapper>
  );
};

const TwoColumn = styled.div`
  display: flex;
  flex-grow: 1;
  width: 600px;
`;

export const Admins: FC<UserPickerProps> = ({ orgId, users }) => {
  const admins = filter(users, user => includes(user.roleIds, 'system-admin'));
  const sortedAdmins = sortBy(admins, user => user.username.toLowerCase());
  const take10Admins = sortedAdmins.slice(0, 10);

  return (
    <AdminColumn>
      <H3>Admins</H3>
      <div>
        {map(take10Admins, admin => (
          <UserRow key={admin.username} orgId={orgId} user={admin} />
        ))}
      </div>
    </AdminColumn>
  );
};

const AdminColumn = styled.div`
  width: 300px;
`;

type UserPickerProps = {
  orgId: string;
  users: [User];
};

export const UserPicker: FC<UserPickerProps> = ({ orgId, users }) => {
  const [username, setUsername] = useState('');
  const filteredUsers = !username
    ? users
    : filter(users, u => {
        return u.username.toLowerCase().indexOf(username.toLowerCase()) > -1;
      });
  const sortedUsers = sortBy(filteredUsers, user => user.username.toLowerCase());
  const take50Users = sortedUsers.slice(0, 50);
  return (
    <SearchColumn>
      <H3>Search</H3>
      <StyledSearchInput initialValue={username} onChange={evt => setUsername(evt.target.value)} />
      <div>
        {map(take50Users, user => (
          <UserRow key={user.username} orgId={orgId} user={user} />
        ))}
      </div>
    </SearchColumn>
  );
};

const SearchColumn = styled.div`
  width: 300px;
`;

const StyledSearchInput = styled(SearchInput)`
  flex-shrink: 1;
  margin-bottom: 8px;
  min-width: 0;
  width: 100%;
`;

type UserRowProps = {
  orgId: string;
  user: User;
};

export const UserRow: FC<UserRowProps> = ({ orgId, user }) => {
  const [isOpen, setIsOpen] = useState(false);

  const dispatch = useDispatch();

  if (!isOpen) {
    return (
      <UnselectedUser>
        <StyledRow onClick={() => setIsOpen(true)}>{user.username}</StyledRow>
      </UnselectedUser>
    );
  }

  return (
    <SelectedUser>
      <StyledRow isOpen={isOpen} onClick={() => setIsOpen(false)}>
        {user.username}
      </StyledRow>
      <Roles>
        <strong>Roles:</strong> {sortBy(user.roleIds, id => id).join(', ')}
      </Roles>
      <StyledButton onClick={() => dispatch(imitateUser({ orgId, userId: user.id }))}>Imitate</StyledButton>
    </SelectedUser>
  );
};

const StyledRow = styled.div`
  cursor: pointer;

  &:hover {
    color: ${p => p.theme.colors.green400};
  }
`;

const Roles = styled.div`
  font-size: ${p => p.theme.fontSize.small};
  margin-bottom: 4px;
`;

const UnselectedUser = styled.div`
  border: 1px solid transparent;
  padding: 4px;
`;

const SelectedUser = styled.div`
  border: 1px solid ${p => p.theme.colors.gray400};
  border-radius: 4px;
  margin-bottom: 4px;
  padding: 4px;

  ${StyledRow} {
    color: ${p => p.theme.colors.green400};
    margin-bottom: 4px;
  }
`;

const StyledButton = styled(Button)`
  height: 24px;
  padding: 0px 16px;
`;

export default ImitateEditor;
